import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SiQuickbooks } from "react-icons/si";
import { CheckCircle } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { openOAuthPopup, finishOAuthPopup } from "@/lib/redirect";
import { showOAuthResult } from "@/components/integrations/IntegrationCard";
import { IntegrationCard, OwnerActions, statusLabel } from "@/components/integrations/IntegrationCard";
import { HealthNote, useHealth } from "@/components/integrations/Health";

export const QboCard = ({ vendorCount }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const handled = useRef(false);
  const { data } = useQuery({ queryKey: ["qbo"], queryFn: () => api.get("/qbo/status"), refetchOnWindowFocus: "always" });

  useEffect(() => {
    const code = params.get("code"), state = params.get("state"), realmId = params.get("realmId");
    if (params.get("error") && String(state || "").startsWith("qb_")) {
      const message = params.get("error") === "access_denied" ? "QuickBooks connection cancelled" : "QuickBooks connection failed. Try again.";
      if (!finishOAuthPopup({ ok: false, message })) toast.error(message, { id: "qbo-oauth-error" });
      setParams({});
      return;
    }
    if (!code || !realmId || handled.current) return;
    handled.current = true;
    api.post("/qbo/callback", { code, state, realmId })
      .then(() => ({ ok: true, message: "QuickBooks connected — expenses are now live" }))
      .catch((e) => ({ ok: false, message: errText(e, "QuickBooks connection failed") }))
      .then((r) => { if (!finishOAuthPopup(r)) showOAuthResult(r, qc); })
      .finally(() => setParams({}));
  }, [params, api, qc, setParams]);

  const connect = async () => {
    try {
      await openOAuthPopup(async () => (await api.post("/qbo/connect", { redirect_uri: `${window.location.origin}/app/integrations` })).url,
        { onDone: (r) => showOAuthResult(r, qc), onClosed: () => qc.invalidateQueries({ queryKey: ["qbo"] }) });
    } catch (e) { toast.error(errText(e, "Couldn't start QuickBooks connection")); }
  };
  const disconnect = async () => { await api.post("/qbo/disconnect"); qc.invalidateQueries(); toast("QuickBooks disconnected"); };
  const h = useHealth()?.providers?.qbo;
  const broken = data?.connected && h?.status === "needs_reconnect";

  return (
    <IntegrationCard testid="integration-qbo" icon={SiQuickbooks} color="#2CA01C" name="QuickBooks Online" status={statusLabel(data?.connected, h)}
      desc={data?.connected ? "Vendors, bills and invoices synced from QuickBooks (last 3 months, monthly average)." : "Connect QuickBooks so real vendors, bills (A/P) and invoices (A/R) drive the forecast."}>
      <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-4"><CheckCircle className="text-[#2CA01C]" /> {vendorCount ?? "—"} vendors categorized</div>
      {data?.connected && <HealthNote health={h} />}
      <OwnerActions testid="integration-qbo">
      {data?.connected && !broken ? (
        <button data-testid="qbo-disconnect-btn" onClick={disconnect} className="w-full rounded-full border border-[#2A2A2A] py-2.5 text-sm hover:border-[#FF5C7A] hover:text-[#FF5C7A] transition-colors">Disconnect</button>
      ) : (
        <>
          <button data-testid="qbo-connect-btn" onClick={connect} disabled={data && !data.configured}
            className="w-full rounded-full bg-[#2CA01C] py-2.5 text-sm font-semibold hover:shadow-[0_0_20px_rgba(44,160,28,0.6)] transition-shadow disabled:opacity-40">{broken ? "Reconnect QuickBooks" : "Connect QuickBooks"}</button>
          {data && !data.configured && <p data-testid="qbo-connect-pending" className="mt-2 text-[11px] text-[#A3A3A3]">QuickBooks keys not added yet — using sample expenses.</p>}
        </>
      )}
      </OwnerActions>
    </IntegrationCard>
  );
};
